import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Loading from "../../../components/Loading";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import {
  getPriceIndexAdvanced,
  PriceIndexAdvancedData,
  PriceIndexAdvancedParams,
} from "../../../Rest_api/RestApi";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  ChartTable,
} from "../../../components/table/SummaryTable";
import LegendSummarySlotTable from "../../../components/table/SummaryTableSlot";
import { BrushStart, ChartType, LegendKey } from "../../../models/chart_model";
import { AddDatesWithFileName } from "../../../helpers/helper";
import FilterMenu from "../../../components/FilterMenu";
import { ExchangeDataTabs } from "../../Rajasthan/Demand/Data";

const DataTabs = ["comparative", "cumulative"];

const SOURCE_OPTIONS = [
  { key: "tam", label: "TAM" },
  { key: "deep", label: "Deep" },
  { key: "npp non re", label: "NPP Non RE" },
  { key: "isgs re", label: "ISGS RE" },
  { key: "collective", label: "Collective" },
];

let Intervals = [
  {
    label: "Date",
    key: "day" as const,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

const chartSuffixes = [
  {
    suffix: "rsKWh",
    displayName: "Price Index",
    stackSuffix: "rsKWh",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "mwh",
    displayName: "Volume",
    stackSuffix: "mwh",
    chartType: ChartType.Bar,
    unit: "MWh",
    yAxisId: "left",
  },
];

const typeFilterMetrics = chartSuffixes;

const tableUnits: Record<string, string> = {};
chartSuffixes.forEach((curr) => {
  tableUnits[curr.suffix] = curr.unit;
});

const PriceIndexAdvanced = () => {
  const activeTab = useSelector((state: any) => state.switchTabs?.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter?.defaultDates || {}
  );
  const dataTab = useSelector((state: any) => state.viewMode?.activeView);
  const [activefunctionTab, setActivefunctionTab] = useState(DataTabs[1]);
  const [rawData, setRawData] = useState<PriceIndexAdvancedData[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryIntervalData, setSummaryIntervalData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedTypeMetrics, setSelectedTypeMetrics] = useState<any[]>([
    typeFilterMetrics[0],
  ]);
  const [selectedSources, setSelectedSources] = useState<string[]>([
    ...SOURCE_OPTIONS.map((s) => s.key),
  ]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const effectiveInterval = selectedInterval;

  const fetchData = useCallback(async () => {
    if (!activeTab || !DefaultDates || !DefaultDates[activeTab] || !DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    const dates = DefaultDates[activeTab]?.defaultRangeDate;
    if (!dates) {
      return;
    }

    setLoading(true);
    try {
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const sourcesParam =
        selectedSources.length === SOURCE_OPTIONS.length
          ? undefined
          : selectedSources;

      const data = await getPriceIndexAdvanced({
        start_date: startDateStr,
        end_date: endDateStr,
        function_type: activefunctionTab as "cumulative" | "comparative",
        sources: sourcesParam,
      } as PriceIndexAdvancedParams);

      setRawData(data);
    } catch (error) {
      console.error("Error fetching price index data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      dispatch(setLoadingFilterName(""));
    }
  }, [
    DefaultDates,
    activeTab,
    activefunctionTab,
    selectedSources,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processDataForInterval = useCallback(
    (sourceData: PriceIndexAdvancedData[], intervalConfig: (typeof Intervals)[number]) => {
      let filteredData = [...sourceData];

      // Filter by selected sources in comparative mode
      if (activefunctionTab === "comparative" && selectedSources.length < SOURCE_OPTIONS.length) {
        filteredData = filteredData.filter((item) =>
          item.source_type && selectedSources.includes(item.source_type)
        );
      }

      // For week and month, we need to aggregate daily data
      if (intervalConfig.key === "week" || intervalConfig.key === "month") {
        const aggregationMap: Record<string, Record<string, { mwh: number; weightedSum: number }>> = {};

        filteredData.forEach((item) => {
          const date = new Date(item.date);
          let groupKey: string;

          if (intervalConfig.key === "week") {
            // Calculate week start (Sunday)
            const weekStart = new Date(date);
            const dayOfWeek = date.getDay();
            weekStart.setDate(date.getDate() - dayOfWeek);
            weekStart.setHours(0, 0, 0, 0);
            groupKey = weekStart.toISOString().split('T')[0];
          } else {
            // Month
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            groupKey = monthYear;
          }

          const prefix = activefunctionTab === "cumulative"
            ? "Cumulative"
            : item.source_type || "Unknown";

          if (!aggregationMap[groupKey]) {
            aggregationMap[groupKey] = {};
          }

          if (!aggregationMap[groupKey][prefix]) {
            aggregationMap[groupKey][prefix] = { mwh: 0, weightedSum: 0 };
          }

          const mwh = item.mwh || 0;
          const rsKWh = item.rsKWh || 0;
          aggregationMap[groupKey][prefix].mwh += mwh;
          aggregationMap[groupKey][prefix].weightedSum += mwh * rsKWh;
        });

        const groupedData: any = {};

        Object.keys(aggregationMap).sort().forEach((groupKey) => {
          const date = new Date(groupKey + (intervalConfig.key === "month" ? "-01" : ""));
          
          let displayKey: string;
          let secondXAxis: string | null = null;

          if (intervalConfig.key === "week") {
            const weekStart = new Date(date);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            displayKey = weekStart.toISOString().split('T')[0];
            secondXAxis = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          } else {
            displayKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          }

          Object.keys(aggregationMap[groupKey]).forEach((prefix) => {
            const agg = aggregationMap[groupKey][prefix];
            const avgRsKWh = agg.mwh > 0 ? agg.weightedSum / agg.mwh : 0;

            const key = `${groupKey}_${prefix}`;
            groupedData[key] = {
              date: groupKey,
              x_axis: displayKey,
              second_x_axis: secondXAxis,
            };

            selectedTypeMetrics.forEach((typeMetric) => {
              const dataKey = `${prefix}_${typeMetric.suffix}`;
              if (typeMetric.suffix === "rsKWh") {
                groupedData[key][dataKey] = avgRsKWh;
              } else if (typeMetric.suffix === "mwh") {
                groupedData[key][dataKey] = agg.mwh;
              }
            });
          });
        });

        const formattedChartData = Object.values(groupedData).sort((a: any, b: any) => {
          const dateA = new Date(a.date + (intervalConfig.key === "month" ? "-01" : ""));
          const dateB = new Date(b.date + (intervalConfig.key === "month" ? "-01" : ""));
          return dateA.getTime() - dateB.getTime();
        });

        const keys = activefunctionTab === "cumulative"
          ? ["Cumulative"]
          : Array.from(
              new Set(
                filteredData
                  .map((item) => item.source_type || "Unknown")
                  .filter(Boolean)
              )
            );

        return {
          chartData: formattedChartData,
          tableData: filteredData,
          chartKeys: keys,
        };
      }

      // For day interval, process normally
      const groupedData: any = {};

      filteredData.forEach((item) => {
        // Group by date - in cumulative mode there's one record per date,
        // in comparative mode there are multiple records per date (one per source)
        const key = item.date;

        if (!groupedData[key]) {
          groupedData[key] = {
            date: item.date,
            x_axis: item.date,
            second_x_axis: null,
          };
        }

        const prefix = activefunctionTab === "cumulative"
          ? "Cumulative"
          : item.source_type || "Unknown";

        selectedTypeMetrics.forEach((typeMetric) => {
          const dataKey = `${prefix}_${typeMetric.suffix}`;
          // API already handles aggregation for cumulative mode, so just assign
          groupedData[key][dataKey] = item[typeMetric.suffix as keyof PriceIndexAdvancedData] as number;
        });
      });

      const formattedChartData = Object.values(groupedData).sort((a: any, b: any) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });

      const keys = activefunctionTab === "cumulative"
        ? ["Cumulative"]
        : Array.from(
            new Set(
              filteredData
                .map((item) => item.source_type || "Unknown")
                .filter(Boolean)
            )
          );

      return {
        chartData: formattedChartData,
        tableData: filteredData,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      selectedSources,
      selectedTypeMetrics,
    ]
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSummaryIntervalData([]);
      return;
    }

    const processed = processDataForInterval(rawData, effectiveInterval);
    setChartData(processed.chartData);
    setChartKeys(processed.chartKeys);
    setTableData(processed.tableData);
    setSummaryIntervalData(processed.chartData);
  }, [
    rawData,
    effectiveInterval,
    processDataForInterval,
  ]);

  // Helper function to get the display label for a source key
  const getSourceLabel = (key: string): string => {
    if (key === "Cumulative") {
      return "Cumulative";
    }
    const sourceOption = SOURCE_OPTIONS.find((opt) => opt.key === key);
    return sourceOption ? sourceOption.label : key;
  };

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string
  ) => {
    const displayLabel = getSourceLabel(key);
    return {
      dataKey: `${key}_${suffix}`,
      name: `${displayLabel} ${displayName}`,
      legendColor: undefined,
      stroke: undefined,
      fill: undefined,
      type: chartType,
      unit: unit,
      yAxisId: yAxisId,
      stackId:
        stackSuffix !== undefined && chartType === ChartType.Bar
          ? stackSuffix
          : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      selectedTypeMetrics.forEach((typeMetric) => {
        legends.push(
          createChartObject(
            key,
            index,
            typeMetric.suffix,
            typeMetric.displayName,
            typeMetric.stackSuffix,
            typeMetric.chartType,
            typeMetric.unit,
            typeMetric.yAxisId
          )
        );
      });
    });

    return legends;
  }

  const memoizedLegends = useMemo(
    () => getLegends(),
    [chartKeys, selectedTypeMetrics]
  );

  const priceLegends = useMemo(
    () =>
      memoizedLegends.map((legend: LegendKey) =>
        legend.unit === "Rs/KWh" ? legend : null
      ),
    [memoizedLegends]
  );

  const xDataKey = effectiveInterval.key === "day" ? "date" : "x_axis";

  const viewExtraKeys = effectiveInterval.key === "week"
    ? ["x_axis", "second_x_axis"]
    : ["date"];

  const viewExtraLabels = effectiveInterval.key === "week"
    ? ["Date", "Week"]
    : ["Date"];

  const summaryExtraKeys = effectiveInterval.key === "week"
    ? ["x_axis", "second_x_axis"]
    : ["date"];

  const summaryExtraLabels = effectiveInterval.key === "week"
    ? ["Date", "Week"]
    : ["Date"];

  const tablePar = useMemo(() => {
    const dates = activeTab && DefaultDates && DefaultDates[activeTab]?.defaultRangeDate;
    return {
      legends: memoizedLegends,
      data: chartData,
      tableData: TableData,
      itemsPerPage: 96,
      extraKeys: viewExtraKeys,
      extraLabels: viewExtraLabels,
      filename: dates
        ? AddDatesWithFileName({
            fileName: "Price Index Advanced",
            dates: dates,
          })
        : "Price Index Advanced",
      customUnits: tableUnits,
      showTotal: false
    };
  }, [
    memoizedLegends,
    chartData,
    TableData,
    viewExtraKeys,
    viewExtraLabels,
    DefaultDates,
    activeTab,
  ]);

  function secondDataKey() {
    if (effectiveInterval.key === "week") {
      return "second_x_axis";
    } else {
      return undefined;
    }
  }

  return (
    <>
      <div className="gas-page-container">
        {loading && <Loading />}
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
              }}
            />

            <FilterMenu
              title="Function"
              items={DataTabs.map(tab => tab.charAt(0).toUpperCase() + tab.slice(1))}
              selectedItems={[activefunctionTab.charAt(0).toUpperCase() + activefunctionTab.slice(1)]}
              onSelect={(item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />

            <FilterMenu
              title="Source"
              items={SOURCE_OPTIONS.map((item) => item.label)}
              selectedItems={SOURCE_OPTIONS
                .filter((opt) => selectedSources.includes(opt.key))
                .map((opt) => opt.label)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedKeys = SOURCE_OPTIONS
                  .filter((opt) => selectedItems.includes(opt.label))
                  .map((opt) => opt.key);
                
                setSelectedSources(newSelectedKeys);
              }}
            />

            <FilterMenu
              title="Type"
              items={typeFilterMetrics.map((item) => item.displayName)}
              selectedItems={selectedTypeMetrics.map((item) => item.displayName)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedMetrics = typeFilterMetrics.filter((item) => {
                  const isSelected = selectedItems.includes(item.displayName);
                  return isSelected;
                });
                setSelectedTypeMetrics(newSelectedMetrics);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          <div className="gas-chart-container">
            <div className="gas-heading gap-x-10">
              <h2 className="chartHeading w-60">Price Index</h2>
            </div>

            {dataTab.label === "Chart" && (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={BrushStart.Start}
                isTimeSlot={false}
                onlyTitle={true}
                legends={memoizedLegends}
                secondYAxisDomain={[0, 10]}
                secondYAxisLabel="Rs/KWh"
                yAxisLabel="MWh"
                xDataKey={xDataKey}
                secondXDataKey={secondDataKey()}
              />
            )}
            {dataTab.label === "Summary" && (
              <LegendSummarySlotTable
                legends={memoizedLegends}
                avgData={chartData}
                minMaxData={
                  summaryIntervalData.length > 0 ? summaryIntervalData : chartData
                }
                avgTableData={TableData}
                extraKeys={summaryExtraKeys}
                extraLabels={summaryExtraLabels}
                unit="MWh"
                priceLegends={priceLegends}
                volumeUnitKey="mwh"
                showWtdAvg={true}
                priceType="wt_avg"
                showTotal={false}
              />
            )}
            {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-2 justify-between gap-5 ">
              <p className="gna-footer-text col-span-1">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default PriceIndexAdvanced;

